/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { type ReactElement, useEffect, forwardRef, useMemo, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { cssMap, jsx } from '@atlaskit/css';
import { ErrorMessage, Field, RequiredAsterisk } from '@atlaskit/form';
import { cssMap as unboundedCssMap, cx } from '@compiled/react';
import Select, {
	type OptionsType,
	type FormatOptionLabelMeta,
	components,
	type MenuListComponentProps,
	type SelectComponentsConfig,
	CheckboxOption,
	type ValueType,
	type ActionMeta,
} from '@atlaskit/select';
import Button from '@atlaskit/button';
import EditorPanelIcon from '@atlaskit/icon/core/migration/status-information--editor-panel';
import { Box, Flex, Inline } from '@atlaskit/primitives/compiled';
import { B50, B100, B300, N0, N30, N40, N200, N900 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { messages } from '../i18n';
import { getProductIcon, JiraSoftwareProduct, JiraWorkManagementProduct } from '../../utils';
import { type ProductSelectOption, ZIndex } from '../../types';
import { useProducts } from './ProductSelectContext';
import {
	mapProductIdsToProductSelectOptions,
	removeJiraCoreIfImplied,
	hasNonCoreJira,
} from './ProductSelectUtils';
import { useAnalytics } from '../../analytics';
import { ProductSelectAnalytics } from './ProductSelectAnalytics';
import type { AtlaskitSelectRefType, GroupType } from '@atlaskit/select/types';
import { fg } from '@atlaskit/platform-feature-flags';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import {
	type CollectionGroupType,
	getGroupedProductOptions,
} from '../../experiments/twcForCoreInvites/getGroupedProductOptions';
import { useInvitePeopleContext } from '../InvitePeople';

const MAX_VISIBLE_PRODUCTS = 2;

const styles = cssMap({
	fieldWrapper: {
		position: 'relative',
	},
	iconWrapper: {
		width: '28px',
		height: '28px',
		borderRadius: token('radius.small', '3px'),
		marginLeft: token('space.050', '4px'),
		marginRight: token('space.100', '8px'),
		paddingBlock: token('space.025', '2px'),
		paddingInline: token('space.025', '2px'),
		overflow: 'hidden',
	},
	labelWrapper: {
		flex: '1 1 auto',
	},
	readOnlyLabelWrapper: {
		color: token('color.text', '#292A2E'),
	},
	label: {
		display: 'block',
		font: token(
			'font.heading.xxsmall',
			'normal 600 12px/16px ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Ubuntu, system-ui, "Helvetica Neue", sans-serif',
		),
		fontWeight: token('font.weight.semibold', '600'),
		color: token('color.text.subtlest', '#626F86'),
		marginTop: token('space.0', '0px'),
		marginBottom: token('space.050', '4px'),
	},
	productValues: {
		flex: '1 1 auto',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
	},
	chooseAllButtonWrapper: {
		paddingBlock: token('space.100', '8px'),
		paddingInlineStart: token('space.250', '20px'),
		paddingInlineEnd: token('space.150', '12px'),
	},
	groupTooltip: {
		textAlign: 'center',
	},
});

const controlStyles = unboundedCssMap({
	root: {
		cursor: 'pointer',
		backgroundColor: token('color.background.input', N0),
		height: '48px',
		paddingLeft: token('space.150', '12px'),
		borderRadius: token('radius.small', '3px'),
		borderColor: token('color.border', N40),
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered', N40),
			borderColor: 'transparent',
		},
	},
	menuIsOpen: {
		backgroundColor: token('color.background.selected', B50),
		'&:hover': {
			backgroundColor: token('color.background.selected', B50),
		},
	},
	focused: {
		backgroundColor: token('color.background.neutral', N30),
		borderColor: token('color.border.focused', B100),
		'&:hover': {
			borderColor: token('color.border.focused', B100),
		},
	},
	menuIsOpenFocused: {
		borderColor: 'transparent',
		'&:hover': {
			borderColor: 'transparent',
		},
	},
});

const valueContainerStyles = unboundedCssMap({
	root: {
		color: token('color.text', N900),
		display: 'flex',
		flexWrap: 'nowrap',
		minWidth: token('space.0'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		'> span > span:nth-child(n + 3)': {
			display: 'none',
		},
	},
	menuIsOpen: {
		color: token('color.text.brand', B300),
	},
});

type ProductOption = ProductSelectOption;

export interface OwnProps {
	onMenuOpen: () => void;
	productOptions?: ProductSelectOption[];
	defaultProductOptions?: ProductOption[];
	formInvalid?: boolean;
}

const isJiraCoreImplied = (option: ProductOption, selectedOptions: OptionsType<ProductOption>) =>
	option.value === JiraWorkManagementProduct.id &&
	hasNonCoreJira(selectedOptions.map((option) => option.value));

const customOptionLabel = (
	option: ProductOption,
	{ context, selectValue }: FormatOptionLabelMeta<ProductOption>,
) => {
	let LabelIcon = getProductIcon(option.value);

	// If the label is Jira Work Management (due to JSW and JWM is shown in same place)
	// then it should render original JWM icon instead of the rebranded Jira icon.
	const isJiraWorkManagement = option.label === JiraWorkManagementProduct.label;
	if (isJiraWorkManagement) {
		LabelIcon = JiraWorkManagementProduct.icon;
	}

	if (context === 'menu') {
		const isImpliedJiraCore = isJiraCoreImplied(option, selectValue);

		return (
			<Tooltip
				content={
					isImpliedJiraCore ? (
						<FormattedMessage
							{...(fg('product-terminology-refresh')
								? messages.optionIncludedWithOtherJiraProductsAppify
								: messages.optionIncludedWithOtherJiraProducts)}
						/>
					) : undefined
				}
				position={'right'}
			>
				<Inline testId={`test-id-option-${option.value}`} alignBlock="center">
					<Box
						xcss={styles.iconWrapper}
						backgroundColor={
							!isJiraWorkManagement && fg('platform-logo-rebrand')
								? undefined
								: 'color.background.neutral'
						}
					>
						<LabelIcon size="small" appearance="brand" />
					</Box>

					<Inline xcss={cx(styles.labelWrapper, option.isReadOnly && styles.readOnlyLabelWrapper)}>
						{option.label}
					</Inline>
					{isImpliedJiraCore && (
						<EditorPanelIcon label={'EditorPanelIcon'} color={token('color.icon.subtle', N200)} />
					)}
				</Inline>
			</Tooltip>
		);
	}
	return option.label;
};

const ProductSelect = forwardRef<AtlaskitSelectRefType, OwnProps>(
	({ onMenuOpen, formInvalid }, ref) => {
		const firedGroupRendered = useRef<boolean>(false);
		const { formatMessage } = useIntl();
		const analytics = useAnalytics(ProductSelectAnalytics);
		const { jiraSubProduct, productId } = useInvitePeopleContext();
		const { selectedProducts, setSelectedProducts, availableProductOptions } = useProducts();

		const [isInvalid, setIsInvalid] = React.useState(false);
		const [isOpen, setIsOpen] = React.useState(false);

		useEffect(() => {
			analytics.productSelectRendered(availableProductOptions);
		}, [availableProductOptions, analytics]);

		const shouldGroupOptions = useMemo(
			() => availableProductOptions.filter(({ group }) => Boolean(group)).length,
			[availableProductOptions],
		);

		const options = useMemo(
			() =>
				shouldGroupOptions
					? getGroupedProductOptions(
							formatMessage,
							availableProductOptions,
							productId === 'jira' ? jiraSubProduct : productId,
						)
					: availableProductOptions,
			[shouldGroupOptions, formatMessage, availableProductOptions, productId, jiraSubProduct],
		);

		useEffect(() => {
			if (!firedGroupRendered.current && shouldGroupOptions) {
				firedGroupRendered.current = true;
				analytics.groupsRendered(selectedProducts, options);
			}
		}, [analytics, shouldGroupOptions, options, selectedProducts]);

		useEffect(() => {
			if (formInvalid && (!selectedProducts || !selectedProducts.length)) {
				setIsInvalid(formInvalid);
			}
		}, [formInvalid, selectedProducts]);

		// Disable JWM if another jira product is selected
		const isOptionDisabled = React.useCallback(
			(option: ProductOption) => {
				return (
					(option.value === JiraWorkManagementProduct.id && hasNonCoreJira(selectedProducts)) ||
					!!option.isReadOnly
				);
			},
			[selectedProducts],
		);
		const isOptionSelected = (
			option: ProductOption,
			selectedOptions: OptionsType<ProductOption>,
		) => {
			return (
				selectedOptions.some((currentOption) => option.value === currentOption.value) ||
				isJiraCoreImplied(option, selectedOptions)
			);
		};

		const handleOnChangeProduct = React.useCallback(
			(values: ValueType<ProductOption, true>, _action: ActionMeta<ProductOption>) => {
				if (!values) {
					return;
				}

				const selectedOptions = [...values];

				setSelectedProducts(removeJiraCoreIfImplied(selectedOptions.map((option) => option.value)));
				setIsInvalid(!(selectedOptions && selectedOptions.length));
			},
			[setSelectedProducts],
		);

		const customMenuListFactory =
			({
				onSelectAll = (_allProducts: ProductOption[]) => {},
			}): React.FC<MenuListComponentProps<ProductOption, true>> =>
			(props) => {
				const onClickSelectAll = () => {
					const allOptions = [...props.options] as ProductOption[];
					props.setValue(allOptions, 'select-option');
					onSelectAll(allOptions);
				};

				// If has grouping, hide select all
				const hasGrouping = (props.options as GroupType<ProductOption>[]).some(
					(item) => item?.options,
				);

				if (hasGrouping) {
					return <components.MenuList {...props} />;
				}

				return (
					<components.MenuList {...props}>
						<Box xcss={styles.chooseAllButtonWrapper}>
							<Button
								onMouseDown={onClickSelectAll}
								appearance="link"
								spacing="none"
								// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
								style={{ fontWeight: token('font.weight.regular') }}
							>
								<FormattedMessage {...messages.selectAllOption} />
							</Button>
						</Box>
						{props.children}
					</components.MenuList>
				);
			};

		const handleMenuOpen = React.useCallback(() => {
			setIsOpen(true);
			if (onMenuOpen) {
				onMenuOpen();
			}
		}, [onMenuOpen]);

		const handleMenuClose = () => setIsOpen(false);

		const customComponents = React.useMemo<
			SelectComponentsConfig<ProductOption | ProductOption, true>
		>(
			() => ({
				MenuList: customMenuListFactory({
					onSelectAll: (selectedProducts) => {
						analytics.selectAllClicked(selectedProducts.map((p) => String(p.value)));
						setIsOpen(false);
					},
				}),

				GroupHeading: (props) => {
					const { tooltip } = props.data as CollectionGroupType<ProductOption>;
					if (tooltip) {
						return (
							<components.GroupHeading {...props}>
								<Flex gap="space.050" alignItems="center">
									{props.children}
									<Tooltip content={<Box xcss={styles.groupTooltip}>{tooltip}</Box>}>
										<InformationCircleIcon label={tooltip} />
									</Tooltip>
								</Flex>
							</components.GroupHeading>
						);
					}
					return <components.GroupHeading {...props} />;
				},

				MultiValue: (props) => {
					const selectValue = (props.selectProps.value as []) || [];

					// `index` *is* passed by react-select, despite not being in MultiValueProps
					const { data, index } = props as typeof props & { index: number };

					const isLast = index === selectValue.length - 1;

					let suffix = ',';
					if (isLast) {
						suffix = '';
					} else if (index >= 1) {
						suffix = ', ...';
					}

					return (
						<span>
							{data.label}
							{suffix}&nbsp;
						</span>
					);
				},

				Option: (props) => {
					return <CheckboxOption {...props} />;
				},

				ValueContainer: ({ children, ...props }) => {
					const selectedOptions = (children as [ReactElement[], ReactElement])[0];
					return (
						<components.ValueContainer
							{...props}
							xcss={cx(
								valueContainerStyles.root,
								props.selectProps.menuIsOpen && valueContainerStyles.menuIsOpen,
							)}
						>
							<Inline as="span" xcss={styles.productValues}>
								{children}
							</Inline>
							<span>
								{props.hasValue &&
									selectedOptions.length > MAX_VISIBLE_PRODUCTS &&
									`(+${selectedOptions.length - MAX_VISIBLE_PRODUCTS})`}
							</span>
						</components.ValueContainer>
					);
				},
				Control: (props) => {
					return (
						<components.Control
							{...props}
							xcss={cx(
								controlStyles.root,
								props.menuIsOpen && controlStyles.menuIsOpen,
								props.isFocused && controlStyles.focused,
								props.menuIsOpen && props.isFocused && controlStyles.menuIsOpenFocused,
							)}
						/>
					);
				},
			}),
			[analytics],
		);

		const customStyles = {
			// main area
			control: (provided: any, state: any) => ({
				...provided,
				cursor: 'pointer',
				background: state.selectProps.menuIsOpen
					? token('color.background.selected', B50)
					: state.isFocused
						? token('color.background.neutral', N30)
						: token('color.background.input', N0),
				height: '48px',
				paddingLeft: token('space.150', '12px'),
				borderRadius: token('radius.small', '3px'),
				borderColor: state.isFocused
					? state.selectProps.menuIsOpen
						? 'transparent'
						: token('color.border.focused', B100)
					: token('color.border', N40),
				':hover': {
					background: state.selectProps.menuIsOpen
						? token('color.background.selected', B50)
						: token('color.background.neutral.hovered', N40),
					borderColor: state.isFocused
						? state.selectProps.menuIsOpen
							? 'transparent'
							: token('color.border.focused', B100)
						: 'transparent',
				},
			}),
			valueContainer: (provided: any, state: any) => ({
				...provided,
				color: state.selectProps.menuIsOpen
					? token('color.text.brand', B300)
					: token('color.text', N900),
				display: 'flex',
				flexWrap: 'nowrap',
				minWidth: 0,
				[`> span > span:nth-child(n + ${MAX_VISIBLE_PRODUCTS + 1})`]: {
					display: 'none',
				},
			}),
			placeholder: (provided: any, state: any) => ({
				...provided,
				color: state.selectProps.menuIsOpen
					? token('color.text.brand', B300)
					: token('color.text', N900),
			}),
			dropdownIndicator: (provided: any, state: any) => ({
				...provided,
				color: state.selectProps.menuIsOpen
					? token('color.text.brand', B300)
					: token('color.text', N900),
				':hover': 'none',
			}),
			menuPortal: (provided: any) => ({ ...provided, zIndex: ZIndex.FLOATING }),
		};

		const selectedValue = React.useMemo(() => {
			const selectedOptions = mapProductIdsToProductSelectOptions(selectedProducts);

			const hasBothJSWAndJWM =
				availableProductOptions.find(({ value }) => value === JiraWorkManagementProduct.id) &&
				availableProductOptions.find(({ value }) => value === JiraSoftwareProduct.id);

			// If JSW and JWM are both shown in options,
			// it should show original label of JWM as Jira Work Management instead of rebranded Jira label
			return hasBothJSWAndJWM
				? selectedOptions.map((option) =>
						option.value === JiraWorkManagementProduct.id
							? {
									label: JiraWorkManagementProduct.label,
									value: option.value,
								}
							: option,
					)
				: selectedOptions;
		}, [selectedProducts, availableProductOptions]);

		const errorMessage = fg('product-terminology-refresh')
			? messages.noProductSelectedMessageAltAppify
			: messages.noProductSelectedMessage;

		return (
			<Field name="invite-people-product-select-list">
				{(formProps) => (
					<Box testId="test-id-invite-people-select-product" xcss={styles.fieldWrapper}>
						<Box as="label" htmlFor="product-select" xcss={styles.label}>
							{selectedProducts.length === 0 ? (
								<FormattedMessage
									{...(fg('product-terminology-refresh')
										? messages.productSelectNoCurrentProductAppify
										: messages.productSelectNoCurrentProduct)}
								/>
							) : (
								<FormattedMessage
									{...(fg('product-terminology-refresh')
										? messages.productSelectAppify
										: messages.productSelect)}
								/>
							)}
							<RequiredAsterisk />
						</Box>
						{
							// We have to use ordinary Select because CheckboxSelect
							// does not allow to override the Option component
						}
						<Select<ProductOption, true>
							isMulti
							styles={customStyles}
							isClearable={false}
							isSearchable={false}
							name="product-select"
							inputId="product-select"
							descriptionId={`${formProps.fieldProps.id}-error`}
							placeholder={
								<FormattedMessage
									{...(fg('product-terminology-refresh')
										? messages.selectPlaceholderAppify
										: messages.selectPlaceholder)}
								/>
							}
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
							className="checkbox-select"
							classNamePrefix="select"
							closeMenuOnSelect={false}
							components={customComponents}
							hideSelectedOptions={false}
							options={options}
							value={selectedValue}
							formatOptionLabel={customOptionLabel}
							onChange={handleOnChangeProduct}
							isOptionDisabled={isOptionDisabled}
							isOptionSelected={isOptionSelected}
							onMenuOpen={handleMenuOpen}
							onMenuClose={handleMenuClose}
							menuIsOpen={isOpen}
							validationState={isInvalid ? 'error' : 'default'}
							menuPortalTarget={document.body}
							menuPosition={'absolute'}
							isRequired={true}
							ref={ref}
						/>
						{isInvalid ? (
							<ErrorMessage>
								<FormattedMessage {...errorMessage} />
							</ErrorMessage>
						) : null}
					</Box>
				)}
			</Field>
		);
	},
);

export default ProductSelect;
